import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { PlusIcon, ArrowTopRightOnSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { toast } from 'react-hot-toast'

const STATUS_STYLES = {
  draft:        'bg-gray-100 text-gray-700',
  submitted:    'bg-amber-100 text-amber-800',
  provisioning: 'bg-blue-100 text-blue-800',
  provisioned:  'bg-emerald-100 text-emerald-700',
  failed:       'bg-red-100 text-red-700',
}

const DELETABLE_STATUSES = new Set(['draft', 'failed'])

/** Best-effort label when display_name / slug not set yet (e.g. fresh draft). */
function rowLabel(r) {
  const bp = r.businessProfile && typeof r.businessProfile === 'object' ? r.businessProfile : null
  const draftBiz = r.draftJson?.business && typeof r.draftJson.business === 'object' ? r.draftJson.business : null
  const draftGi = r.draftJson?.groupInfo
  const fromDraft =
    (typeof draftGi === 'object' && (draftGi?.groupName || draftGi?.legalName)) || null
  const name =
    (r.displayName && String(r.displayName).trim())
    || (bp?.name && String(bp.name).trim())
    || (bp?.legal_name && String(bp.legal_name).trim())
    || (draftBiz?.name && String(draftBiz.name).trim())
    || (draftBiz?.legal_name && String(draftBiz.legal_name).trim())
    || (fromDraft && String(fromDraft).trim())
    || null
  if (name) return name
  if (r.tenantSlug) return String(r.tenantSlug)
  return `Draft #${r.id}`
}

export default function PendingTenantSetups() {
  const [rows, setRows] = useState(null)
  const [creating, setCreating] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const navigate = useNavigate()

  const load = async () => {
    try {
      const r = await authFetch(getLocalApiUrl('/api/admin/pending-tenant-setups'))
      const j = await r.json()
      if (j.success) setRows(j.setups || [])
      else toast.error(j.error || 'Failed to load')
    } catch (e) {
      toast.error(e.message)
    }
  }

  useEffect(() => { load() }, [])

  const createNew = async () => {
    setCreating(true)
    try {
      const stamp = new Date().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
      const r = await authFetch(getLocalApiUrl('/api/admin/pending-tenant-setups'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: `New tenant setup · ${stamp}` }),
      })
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'create failed')
      navigate(`/admin/new-tenant-setup/${j.setup.id}`)
    } catch (e) {
      toast.error(e.message)
    } finally {
      setCreating(false)
    }
  }

  const removeDraft = async (row) => {
    const label = rowLabel(row) || `draft #${row.id}`
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return
    setDeletingId(row.id)
    try {
      const r = await authFetch(getLocalApiUrl(`/api/admin/pending-tenant-setups/${row.id}`), { method: 'DELETE' })
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'delete failed')
      toast.success('Draft removed')
      setRows((rs) => (rs || []).filter((x) => x.id !== row.id))
    } catch (e) {
      toast.error(e.message)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="p-6 max-w-6xl">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 max-w-3xl">
          <h1 className="text-2xl font-semibold text-gray-900">Pending tenant setups</h1>
          <p className="text-sm text-gray-600 mt-1 leading-relaxed">
            Capture a new tenant's onboarding data before the tenant DB is provisioned.
            Each row holds the same fields the tenant-side wizard collects; submit + provision
            creates the tenant and hydrates its venue configuration in one step.
          </p>
        </div>
        <button
          type="button"
          onClick={createNew}
          disabled={creating}
          className="inline-flex shrink-0 items-center gap-1 px-4 py-2 text-sm bg-psg-teal text-white rounded-md disabled:opacity-50 self-start"
        >
          <PlusIcon className="h-4 w-4" />
          New tenant setup
        </button>
      </div>

      {rows === null ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed rounded-md p-6 text-center text-sm text-gray-500">
          No drafts yet. Click <strong>New tenant setup</strong> to start.
        </div>
      ) : (
        <div className="border rounded-md overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Display name</th>
                <th className="px-3 py-2 text-left">Tenant slug</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Updated</th>
                <th className="px-3 py-2 text-left">Provisioned group</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const label = rowLabel(r)
                const canDelete = DELETABLE_STATUSES.has(r.status)
                return (
                <tr key={r.id} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-900">{label}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-700">
                    {r.tenantSlug || <span className="text-gray-400 font-sans">Set in wizard</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[r.status] || 'bg-gray-100 text-gray-700'}`}>
                      {r.status}
                    </span>
                    {r.provisioningError && <div className="text-[10px] text-red-600 mt-0.5">{r.provisioningError}</div>}
                  </td>
                  <td className="px-3 py-2 text-gray-600 text-xs">{r.updatedAt ? new Date(r.updatedAt).toLocaleString() : ''}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.provisionedGroupId
                      ? <Link to={`/admin/tenants/${r.provisionedGroupId}`} className="text-psg-teal hover:underline">{r.provisionedGroupId}</Link>
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-3">
                      <Link
                        to={`/admin/new-tenant-setup/${r.id}`}
                        className="inline-flex items-center gap-1 text-psg-teal hover:underline text-xs"
                      >
                        Open
                        <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                      </Link>
                      <button
                        type="button"
                        onClick={() => removeDraft(r)}
                        disabled={!canDelete || deletingId === r.id}
                        title={canDelete ? 'Delete draft' : `Cannot delete (status: ${r.status})`}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                        {deletingId === r.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
